import { Select as AntSelect } from 'antd';
import styled, { css, keyframes } from 'styled-components';

export const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); box-shadow: 0 0 10px rgba(255, 255, 255, 0.8); }
  100% { transform: scale(1); }
`;

export const OverlayMain = styled.main`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 80vh;
  transition: all 0.5s ease;
`;

export const OverlaySelect = styled.select`
  padding: 10px;
  font-size: 16px;
  border-radius: 5px;
  transition: opacity 0.3s ease;
`;

export const StyledSelect = styled(AntSelect)<{ $isHighlighted: boolean }>`
  width: 100%;
  height: 100%;
  transition: all 0.3s ease-in-out;

  ${({ $isHighlighted }) =>
    $isHighlighted &&
    css`
      animation: ${pulse} 1s ease-in-out infinite;
    `}
`;

export const HeaderBoxCenter = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  padding: 12px 12px 18px 12px;
  background-color: ${({ theme }) => theme.colors.background.elevated};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  display: flex;
  align-items: stretch;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[4]};
  min-height: ${({ theme }) => theme.spacing[12]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  transition: all ${({ theme }) => theme.transitions.duration.base}
    ${({ theme }) => theme.transitions.easing.inOut};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing[3]}
      ${({ theme }) => theme.spacing[6]};
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing[3]}
      ${({ theme }) => theme.spacing[8]};
  }

  /* Enhanced Ant Design Select styling */
  .ant-select {
    width: 100% !important;
    height: ${({ theme }) => theme.spacing[10]} !important;
    border-radius: ${({ theme }) => theme.borderRadius.md} !important;

    .ant-select-selector {
      height: ${({ theme }) => theme.spacing[10]} !important;
      background-color: ${({ theme }) =>
        theme.colors.background.paper} !important;
      border: 1px solid
        ${({ theme }) =>
          theme.mode === 'dark'
            ? theme.colors.neutral[600]
            : theme.colors.divider} !important;
      border-radius: ${({ theme }) => theme.borderRadius.md} !important;
      color: ${({ theme }) => theme.colors.text.primary} !important;
      font-family: ${({ theme }) =>
        theme.typography.fontFamily.secondary} !important;
      font-size: ${({ theme }) =>
        theme.typography.body.medium.fontSize} !important;
      transition: all ${({ theme }) => theme.transitions.duration.fast}
        ${({ theme }) => theme.transitions.easing.inOut} !important;
      box-shadow: ${({ theme }) => theme.shadows.sm} !important;

      &:hover {
        border-color: ${({ theme }) => theme.colors.primary.light} !important;
        box-shadow: ${({ theme }) => theme.shadows.md} !important;
      }

      &:focus,
      &:focus-within {
        border-color: ${({ theme }) => theme.colors.primary.main} !important;
        box-shadow:
          0 0 0 3px ${({ theme }) => theme.colors.primary.main}20,
          ${({ theme }) => theme.shadows.md} !important;
      }
    }

    .ant-select-selection-placeholder {
      color: ${({ theme }) => theme.colors.text.hint} !important;
      font-size: ${({ theme }) =>
        theme.typography.body.medium.fontSize} !important;
    }

    .ant-select-selection-item {
      color: ${({ theme }) => theme.colors.text.primary} !important;
      font-weight: ${({ theme }) =>
        theme.typography.fontWeight.medium} !important;
      display: flex !important;
      align-items: center !important;
    }

    .ant-select-arrow {
      color: ${({ theme }) => theme.colors.text.secondary} !important;
      font-size: 14px !important;
    }

    &.ant-select-open .ant-select-selector {
      border-color: ${({ theme }) => theme.colors.primary.main} !important;
      box-shadow:
        0 0 0 3px ${({ theme }) => theme.colors.primary.main}20,
        ${({ theme }) => theme.shadows.md} !important;
    }
  }

  .ant-select-dropdown {
    background-color: ${({ theme }) =>
      theme.colors.background.paper} !important;
    border: 1px solid
      ${({ theme }) =>
        theme.mode === 'dark'
          ? theme.colors.neutral[600]
          : theme.colors.divider} !important;
    border-radius: ${({ theme }) => theme.borderRadius.lg} !important;
    box-shadow: ${({ theme }) => theme.shadows.xl} !important;
    padding: ${({ theme }) => theme.spacing[1]} 0 !important;
  }

  @media (max-width: 768px) {
    gap: ${({ theme }) => theme.spacing[2]};
    min-height: auto;

    .ant-select {
      height: ${({ theme }) => theme.spacing[9]} !important;

      .ant-select-selector {
        height: ${({ theme }) => theme.spacing[9]} !important;
      }
    }
  }
`;

export const Field = styled.div`
  flex: 1 1 280px; /* cresce, encolhe, base ~280px */
  min-width: 200px;
  max-width: 260px;

  @media (max-width: 768px) {
    flex: 1 1 100%;
    min-width: 100%;
  }
`;

export const ChildrenContainer = styled.div`
  flex-grow: 1;
  flex-basis: 0%;
  flex-shrink: 1;
  min-width: 200px;

  @media (max-width: 768px) {
    flex-basis: 100%;
    min-width: 100%;
  }
`;
